// toast.ts — THE toast primitive (Styles x Themes, PR-3b).
//
// A toast is the product's transient resolution beat: "Spawned: X",
// "Delete refused", "Uploaded as v3". Before PR-3b there were TWO of them and
// one silent hole:
//
//   showToast()               #toast in the shell HTML, ONE boolean (`error`)
//                             for colour, no aria-live, and — the hole —
//                             `if (!el) return`, so on any host whose HTML
//                             lacked the element every toast the product
//                             raised was DROPPED with no error. The KB and API
//                             portals have no #toast; the Bridge home has the
//                             element but its stylesheet has no `.toast` rule
//                             at all, so a pi-stack toast raised inside an RM
//                             chat rendered as unstyled text in the corner.
//   showChromeToast()         release-chrome.ts's own `.rc-toast` node —
//                             fixed bottom-right, 12s, click-to-dismiss,
//                             hard-wired to the risk colour.
//
// This is the one primitive: FOUR INTENTS on the same ramps every button
// rides, an aria-live seat that is polite for good news and assertive for
// bad, auto-dismiss whose duration follows the intent, and a host it CREATES
// when the page does not carry one — a toast that cannot be seen is the same
// class of failure as a gate that cannot refuse.
//
// The reduced-motion arm is in CSS (`.toast` is registered in each surface's
// reduced-motion guard) AND here: under reduced motion the show/hide skip the
// rAF entrance so the text is simply present, never a 200ms fade a user who
// asked for no motion still has to sit through.

export type ToastIntent = 'neutral' | 'additive' | 'destructive' | 'error';

export interface ToastOptions {
  /** Ramp + aria-live urgency. Default 'neutral'. */
  intent?: ToastIntent;
  /**
   * Back-compat with the boolean this primitive replaced: `{ error: true }`
   * is exactly `{ intent: 'error' }`. Kept because ~60 call sites across
   * three surfaces pass it, and rewriting them all in the same PR that
   * changes the mechanism would make a regression unattributable.
   */
  error?: boolean;
  /** Auto-dismiss after this many ms. Defaults follow the intent. */
  ms?: number;
  /** Clicking the toast dismisses it (default true). */
  clickToDismiss?: boolean;
}

/** The element id the shells already carry. */
export const TOAST_HOST_ID = 'toast';

/** Per-intent dwell. Bad news stays long enough to be read and copied; a
 *  routine acknowledgment gets out of the way. */
const DWELL_MS: Record<ToastIntent, number> = {
  neutral: 2600,
  additive: 2600,
  destructive: 6000,
  error: 8000,
};

let hideTimer: ReturnType<typeof setTimeout> | undefined;

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * The toast host. Found by id, or CREATED and appended to <body>.
 *
 * Creating it is deliberate: the previous `if (!el) return` turned "this page
 * has no toast element" into "this page silently swallows every message the
 * product tries to raise", which is indistinguishable from success at the
 * call site. Returns null only when there is no document at all (the
 * stub-document suites), where the caller is not rendering anyway.
 */
export function toastHost(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const found = document.getElementById?.(TOAST_HOST_ID);
  if (found) return found;
  if (!document.body?.appendChild) return null;
  const el = document.createElement('div');
  el.id = TOAST_HOST_ID;
  el.className = 'toast';
  document.body.appendChild(el);
  return el;
}

/**
 * Raise a toast. Returns the host element (or null when there is no document)
 * so a caller can assert against the real node instead of guessing.
 */
export function showToast(message: string, opts: ToastOptions = {}): HTMLElement | null {
  const intent: ToastIntent = opts.intent ?? (opts.error === true ? 'error' : 'neutral');
  const el = toastHost();
  if (!el) return null;
  el.textContent = message;
  el.dataset.toast = intent;
  // The ramp lives on data-toast; `.error` is kept in lockstep for the one
  // release still styling on it, and cleared otherwise so a neutral toast
  // after an error one does not inherit the risk border.
  el.classList.toggle('error', intent === 'error' || intent === 'destructive');
  // aria-live: an error INTERRUPTS, an acknowledgment waits its turn. role
  // and aria-live are set per raise because the urgency is per message.
  el.setAttribute('role', intent === 'error' || intent === 'destructive' ? 'alert' : 'status');
  el.setAttribute('aria-live', intent === 'error' || intent === 'destructive' ? 'assertive' : 'polite');
  el.setAttribute('aria-atomic', 'true');
  if (opts.clickToDismiss !== false) {
    el.style.pointerEvents = 'auto';
    el.onclick = () => { dismissToast(); };
  } else {
    el.style.pointerEvents = 'none';
    el.onclick = null;
  }
  el.classList.add('show');
  if (hideTimer) clearTimeout(hideTimer);
  const ms = opts.ms ?? DWELL_MS[intent];
  hideTimer = setTimeout(() => dismissToast(), ms);
  return el;
}

/** Dismiss the current toast now (the click handler and the timer share this
 *  one path, so a click during the dwell cannot leave a stale timer). */
export function dismissToast(): void {
  if (hideTimer) { clearTimeout(hideTimer); hideTimer = undefined; }
  if (typeof document === 'undefined') return;
  const el = document.getElementById?.(TOAST_HOST_ID);
  if (!el) return;
  el.classList.remove('show');
  // Under reduced motion nothing is animating, so the node's text is cleared
  // at once rather than sitting invisible but present for a screen reader
  // that would otherwise re-announce it on the next DOM read.
  if (reducedMotion()) el.textContent = '';
}
